<template>
  <div class="v-select-input" :class="cssClasses">
    <v-spinner v-if="loading"></v-spinner>
    <label>{{label}}</label>
    <select
      @blur="isFocused = false"
      @focus="isFocused =  true"
      @input="valueChanged($event)"
      @change="valueChanged($event)"
    >
      <option :value="null" :disabled="!hasStyleModifierIssue && isFocused" v-if="!value"></option>
      <slot></slot>
    </select>
    <v-icon icon="chevron-down" :ratio="0.5" />
  </div>
</template>

<script>
import _ from "lodash";
import VIcon from "../icon/Icon.vue";
import VSpinner from "../spinner/Spinner.vue";

export default {
  name: "v-select-input",
  components: {
    VIcon,
    VSpinner
  },
  props: {
    value: {},
    label: { type: String },
    loading: { type: Boolean }
  },
  data() {
    return {
      selectElement: null,
      isFocused: false
    };
  },
  computed: {
    hasStyleModifierIssue() {
      // https://stackoverflow.com/questions/1763113/why-does-select-with-onfocus-not-work-in-ie
      return !!window.MSInputMethodContext && !!document.documentMode;
    },
    cssClasses() {
      let classes = [];
      if (this.isFocused) classes.push("is-focused");
      if (this.value) classes.push("has-value");
      if (this.loading) classes.push("is-loading");
      return classes;
    }
  },
  methods: {
    valueChanged($event) {
      let value = $event.target.value;
      if (value) {
        this.$emit("input", value, $event);
        this.$emit("change", value, $event);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../design/variables.scss";

.v-select-input {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: 1px solid #d5d5d5;
  border-radius: 4px;
  padding-left: 15px;
  padding-bottom: 28px;
  padding-top: 28px;
  cursor: pointer;

  &.is-loading {
    select,
    .v-icon,
    label {
      display: none;
    }
    .v-spinner {
      position: absolute;
      top: 14px;
      left: 50%;
      margin-left: -16px;
    }
  }

  label {
    position: absolute;
    left: 16px;
    top: 0;
    transition: all ease-in-out 60ms;
    transform: translateY(16px);
    font-size: 1rem;
    color: map-deep-get($colors, "foreground", "secondary");
  }
  &.is-focused,
  &.has-value {
    label {
      font-size: 0.7rem;
      transform: translateY(8px);
    }
  }

  &[disabled] {
    cursor: not-allowed;
    border-color: map-deep-get($colors, "ornament", "divider");
    .v-icon {
      color: #aaa;
    }
    &:hover {
      border-color: map-deep-get($colors, "ornament", "divider");
    }
    select {
      pointer-events: none;
      color: map-deep-get($colors, "ornament", "border");
    }
  }

  .v-icon {
    pointer-events: none;
    position: absolute;
    top: 20px;
    right: 15px;
    color: map-deep-get($colors, "foreground", "action");
    transition: all ease-in-out 200ms;
  }

  &.is-focused {
    border-color: map-deep-get($colors, "foreground", "action");
  }
  > select {
    font-family: inherit;
    cursor: pointer;
    position: absolute;
    padding-top: 14px;
    padding-left: 18px;
    @media (min--moz-device-pixel-ratio: 0) {
      padding-left: 14px;
    }
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    font-size: 1rem;
    width: 100%;
    height: 100%;
    z-index: 200;
    background: transparent;
    outline: none;
    border: none;
    appearance: none;
    box-shadow: none;
    &:-moz-focusring {
      color: transparent;
      text-shadow: 0 0 0 map-deep-get($colors, "foreground", "primary");
    }
    &::-ms-expand {
      display: none;
    }
  }
}
</style>
